/**
 * 机上調査の 2 軸集計 (マトリクス、docs/27 §9-3)。
 *
 * エリア / 路線の集計を「調査項目 × 調査項目」のクロス集計で見るための pure 関数。
 * 行・列とも調査項目 (`ResolvedSurveyAxis`) を指定でき、既定は「集計する調査項目 × 評価」。
 * グループ化 (大分類でまとめるか細分類のままか) と色はマスタの `colorAxis` 規約に従う
 * (`computeAreaAxisCounts` と同じ `groupKeyOf` / `groupLabelOf` / `groupColorOf`)。
 */
import type { EvaluationAxisDef } from '@citizen-walk/shared-types';
import {
  groupColorOf,
  groupKeyOf,
  groupLabelOf,
  type AxisGranularity,
} from './computeAreaAxisCounts';
import { resolveMasterRow } from './streetSurveyPinDisplay';
import type { ResolvedSurveyAxis } from './useStreetSurveyConfig';
import type { StreetSurveyPost } from './useStreetSurveyPosts';

export interface MatrixBucket {
  key: string;
  label: string;
  color: string;
}

export interface AxisMatrix {
  rows: MatrixBucket[];
  cols: MatrixBucket[];
  /** counts[行][列]。 */
  counts: number[][];
  rowTotals: number[];
  colTotals: number[];
  total: number;
  /** 行・列のどちらかの値が無い / マスタで解決できなかった検出要素の数。 */
  unresolved: number;
}

const FALLBACK_COLOR = '#94A3B8';

/** その項目が取りうる値 (マスタの行の順)。評価の項目は評価軸の色・表記を使う。 */
function bucketsOf(
  axis: ResolvedSurveyAxis,
  evaluation: EvaluationAxisDef | null,
  granularity: AxisGranularity,
): MatrixBucket[] {
  const isEvaluation = !!evaluation && axis.codeField === evaluation.field;
  const out: MatrixBucket[] = [];
  const seen = new Set<string>();
  for (const row of axis.master?.rows ?? []) {
    if (isEvaluation) {
      const label = evaluation!.labelOf(row.category) || row.category;
      if (!label || seen.has(label)) continue;
      seen.add(label);
      out.push({ key: label, label, color: evaluation!.colorOf(row.category) });
      continue;
    }
    const key = groupKeyOf(row, axis.master?.colorAxis, granularity);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      key,
      label: groupLabelOf(row, axis.master?.colorAxis, key, granularity),
      color: axis.master ? groupColorOf(axis.master, row, key, granularity) : FALLBACK_COLOR,
    });
  }
  return out;
}

/** 1 件の検出要素の値が、どのグループに入るか (解決できなければ null)。 */
function bucketKeyOf(
  value: unknown,
  axis: ResolvedSurveyAxis,
  evaluation: EvaluationAxisDef | null,
  granularity: AxisGranularity,
): string | null {
  if (value === undefined || value === null || String(value) === '') return null;
  if (evaluation && axis.codeField === evaluation.field) {
    return evaluation.labelOf(value) || String(value);
  }
  const row = resolveMasterRow(String(value), axis.byCategoryCd);
  if (!row) return null;
  if (row.excludeFromAggregation) return null;
  return groupKeyOf(row, axis.master?.colorAxis, granularity);
}

/**
 * 選択した地点群の検出要素を、行の項目 × 列の項目で数える。
 * マスタの行から先に枠を作るので、0 件のグループも表に残る (母集団が分かる)。
 */
export function computeAxisMatrix(
  posts: readonly StreetSurveyPost[],
  rowAxis: ResolvedSurveyAxis | null,
  colAxis: ResolvedSurveyAxis | null,
  evaluation: EvaluationAxisDef | null = null,
  granularity: AxisGranularity = 'auto',
): AxisMatrix {
  const rows = rowAxis ? bucketsOf(rowAxis, evaluation, granularity) : [];
  const cols = colAxis ? bucketsOf(colAxis, evaluation, granularity) : [];
  const rowIndex = new Map(rows.map((b, i) => [b.key, i]));
  const colIndex = new Map(cols.map((b, i) => [b.key, i]));
  const counts = rows.map(() => cols.map(() => 0));
  let unresolved = 0;

  if (rowAxis && colAxis) {
    for (const post of posts) {
      for (const c of post.classifications) {
        const rk = bucketKeyOf(c[rowAxis.codeField], rowAxis, evaluation, granularity);
        const ck = bucketKeyOf(c[colAxis.codeField], colAxis, evaluation, granularity);
        const ri = rk !== null ? rowIndex.get(rk) : undefined;
        const ci = ck !== null ? colIndex.get(ck) : undefined;
        if (ri === undefined || ci === undefined) {
          unresolved++;
          continue;
        }
        counts[ri]![ci]! += 1;
      }
    }
  }

  const rowTotals = counts.map((r) => r.reduce((a, b) => a + b, 0));
  const colTotals = cols.map((_, ci) => counts.reduce((sum, r) => sum + (r[ci] ?? 0), 0));
  const total = rowTotals.reduce((a, b) => a + b, 0);
  return { rows, cols, counts, rowTotals, colTotals, total, unresolved };
}
